import { useTranslation } from "@me8eon/translation";
import "./EditBooking.scss";
import {
    EonUiButton,
    EonUiDatePicker,
    EonUiDropdown,
    EonUiDropdownOption,
    EonUiInput,
} from "@eon-ui/eon-ui-components-react";
import useRoomBookingStore from "../../../store/roomBookingStore";
import {
    getBookingContainerTemplateApi,
    getTimeLinesApi,
    saveBookingsApi,
    updateBookingApi,
} from "../../../services/roomBooking";
import React, { useEffect, useRef, useState } from "react";
import {
    convertDurationToMilliseconds,
    getFormattedDateTime,
    formatDuration,
} from "../../../util/date";
import { getAccessToken } from "../../../services/accessToken";
import { Icon, ThemedIcon } from "@me8eon/icons";
import { EonUiInputCustomEvent } from "@eon-ui/eon-ui-components";
import { DURATION_OPTIONS } from "../RoomBookingInterface";
import LoaderComponent from "../../loader-component/LoaderComponent";
import TimePicker from "../../time-picker/TimePicker";

type EditBookingProps = {
    callback: () => void;
    action: "edit" | "rebook";
};

const EditBooking = ({ callback, action }: EditBookingProps) => {
    const {
        currentBookingData,
        setEditBookingModal,
        setRebookingModal,
        manageBookingRawData,
        bookAgainRawData,
        userData,
        userLocalTimezone,
        numberOfParticipantsRebooking,
        setNumberOfParticipantsRebooking,
    } = useRoomBookingStore();

    const { t, i18n } = useTranslation("room-booking");

    const [availabilityCode, setAvailabilityCode] = useState<number>(-1);
    const [meetingTopic, setMeetingTopic] = useState<string>("");
    const [dateValue, setDateValue] = useState<string>("");
    const [dateValueChanged, setDateValueChanged] = useState<boolean>(false);
    const [startTime, setStartTime] = useState<string>("");
    const [duration, setDuration] = useState<string>("");
    const [preFillComplete, setPreFillComplete] = useState<boolean>(false);
    const [scheduleChanged, setScheduleChanged] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
    const [invalidMeetingTopic, setInvalidMeetingTopic] = useState(false);
    const [invalidNumberOfParticipants, setInvalidNumberOfParticipants] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const availabilityRequestIdRef = useRef(0);

    const date = new Date(currentBookingData.meeting_on);

    const options: Intl.DateTimeFormatOptions = {
        weekday: "long",
        day: "2-digit",
        month: "short",
    };

    const currentTime = new Date().getTime();

    useEffect(() => {
        const meeting = getMeetingDetailsById(currentBookingData.meeting_id);
        if (!meeting) return;

        const timer = setTimeout(() => {
            setMeetingTopic(currentBookingData.meeting_desc || "");
            setDateValue(preFillDate(meeting.begin.timestamp));
            setDuration(preFillDuration(meeting.begin.timestamp, meeting.end.timestamp));
            setStartTime(preFillStartTime(meeting.begin.timestamp));
            setNumberOfParticipantsRebooking(currentBookingData.meeting_participants);

            setDateValueChanged(false);
            setScheduleChanged(false);
            setPreFillComplete(true);
        }, 0);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!preFillComplete || !dateValue || !startTime || !duration) return;

        if (action === "edit" && !scheduleChanged) return;

        checkAvailability();
    }, [preFillComplete, dateValue, startTime, duration, scheduleChanged]);

    const preFillDuration = (startTime: number, endTime: number): string => {
        const diffInMinutes = (endTime - startTime) / 60;

        return diffInMinutes < 60
            ? `${diffInMinutes} Min`
            : `${diffInMinutes / 60} Hr`;
    };

    const preFillDate = (startTime: number): string => {
        return action === "edit"
            ? getFormattedDateTime(startTime * 1000, "Y-m-d", false, userLocalTimezone)
            : getFormattedDateTime(currentTime, "Y-m-d", false, userLocalTimezone);
    };

    const preFillStartTime = (startTime: number): string => {
        const nextTime = Math.ceil(currentTime / (15 * 60 * 1000)) * (15 * 60 * 1000);

        return action === "edit"
            ? getFormattedDateTime(startTime * 1000, "H:i", false, userLocalTimezone)
            : getFormattedDateTime(nextTime, "H:i", false, userLocalTimezone);
    };

    const formattedMeetingDate = date
        .toLocaleDateString("en-GB", options)
        .replace(/ /g, " ");

    const markScheduleChanged = () => {
        setScheduleChanged(true);
        setPreFillComplete(true);
    };

    const updateBooking = async (meeting_id: number) => {
        setUpdateSuccess(true);
        setIsLoading(true);

        const actualToken = await getAccessToken();
        const payload = await getUpdateBookingPayload(meeting_id);

        if (!payload) {
            setErrorMessage(t("apiFailDefaultMsg"));
            setUpdateSuccess(false);
            setIsLoading(false);
            return;
        }

        const begin = new Date(`${dateValue} ${startTime}`).getTime();
        const end = begin + convertDurationToMilliseconds(duration);
        const format = "Y-m-d H:i:s";

        payload[0].booking_templates[0].begin.timestamp = begin / 1000;
        payload[0].booking_templates[0].begin.datetime = getFormattedDateTime(
            begin,
            format,
            false,
            userLocalTimezone
        );

        payload[0].booking_templates[0].end.timestamp = end / 1000;
        payload[0].booking_templates[0].end.datetime = getFormattedDateTime(
            end,
            format,
            false,
            userLocalTimezone
        );

        payload[0].booking_templates[0].initial_begin.timestamp = begin / 1000;
        payload[0].booking_templates[0].initial_begin.datetime =
            getFormattedDateTime(begin, format, false, userLocalTimezone);

        const { seating_id } = payload[0].booking_templates[0];
        payload[0].booking_templates[0].seating_id =
            seating_id === 0 ? null : seating_id;

        const finalPayload = {
            include: [],
            booking_container_templates: payload,
            dummytoken: "",
            accesstoken: actualToken || "",
            send_mails: true,
        };

        try {
            const response = await updateBookingApi(finalPayload);

            if (
                response.status === "success" &&
                response.data.booking_container_results[0].result &&
                !response.data.booking_container_results[0].errors.length
            ) {
                setSuccessMessage(t("updateBookingApiSucessMsg"));
            } else {
                setErrorMessage(t("apiFailDefaultMsg"));
                setUpdateSuccess(false);
                console.log("something went wrong!", response);
            }
        } catch (e) {
            setErrorMessage(t("apiFailDefaultMsg"));
            setUpdateSuccess(false);
            console.log("something went wrong!", e);
        } finally {
            setIsLoading(false);
        }
    };

    const getUpdateBookingPayload = async (meeting_id: number) => {
        const meeting = getMeetingDetailsById(meeting_id);
        const actualToken = await getAccessToken();

        if (!meeting) return;

        const payload = {
            booking_container_ids: [meeting.booking_container_id],
            include: [],
            accesstoken: actualToken || "",
        };

        try {
            const response = await getBookingContainerTemplateApi(payload);

            if (
                response.status === "success" &&
                response.data.booking_container_templates.length
            ) {
                return response.data.booking_container_templates;
            }

            console.log("something went wrong!", response);
        } catch (e) {
            console.log("something went wrong!", e);
        }
    };

    const validateBooking = (roomType: number) => {
        let valid = true;

        if (!meetingTopic) {
            setInvalidMeetingTopic(true);
            setErrorMessage(t("meetingTitleMissing"));
            valid = false;
        }

        if (!numberOfParticipantsRebooking && roomType !== 2) {
            setInvalidNumberOfParticipants(true);
            setErrorMessage(t("numberOfParticipantMissing"));
            valid = false;
        }

        return valid;
    };

    const saveBooking = async (meeting_id: number) => {
        setUpdateSuccess(true);
        setIsLoading(true);

        const meeting = getMeetingDetailsById(meeting_id);

        if (!meeting || !validateBooking(meeting.object_type_id)) {
            setUpdateSuccess(false);
            setIsLoading(false);
            return false;
        }

        const actualToken = await getAccessToken();
        const begin = new Date(`${dateValue} ${startTime}`).getTime();
        const end = begin + convertDurationToMilliseconds(duration);
        const format = "Y-m-d H:i:s";
        const { user_id } = userData;

        const payload = {
            booking_container_templates: [
                {
                    user_id,
                    terms: [],
                    booking_templates: [
                        {
                            object_id: meeting.object_id,
                            cost: 0,
                            begin: {
                                datetime: getFormattedDateTime(
                                    begin,
                                    format,
                                    false,
                                    userLocalTimezone
                                ),
                                timezone_id: userLocalTimezone,
                            },
                            end: {
                                datetime: getFormattedDateTime(
                                    end,
                                    format,
                                    false,
                                    userLocalTimezone
                                ),
                                timezone_id: userLocalTimezone,
                            },
                            topic: meetingTopic,
                            capacity: numberOfParticipantsRebooking,
                            accessories: [],
                        },
                    ],
                },
            ],
            send_mails: true,
            dummytoken: "",
            accesstoken: actualToken || "",
        };

        try {
            const response = await saveBookingsApi(payload);

            if (
                response.status === "success" &&
                response.data.booking_container_results[0].result &&
                !response.data.booking_container_results[0].errors.length
            ) {
                setSuccessMessage(t("rebookingApiSucessMsg"));
            } else {
                setErrorMessage(t("apiFailDefaultMsg"));
                setUpdateSuccess(false);
                console.log("something went wrong!", response);
            }
        } catch (e) {
            setErrorMessage(t("apiFailDefaultMsg"));
            setUpdateSuccess(false);
            console.log("something went wrong!", e);
        } finally {
            setIsLoading(false);
        }
    };

    const getMeetingDetailsById = (meeting_id: number) => {
        if (action === "rebook") {
            return bookAgainRawData.find((obj) => obj.booking_id === meeting_id);
        }

        return manageBookingRawData.find((obj) => obj.booking_id === meeting_id);
    };

    const getMeetingDuration = (meeting_id: number) => {
        const meeting = getMeetingDetailsById(meeting_id);
        if (!meeting) return "";

        const diffInSeconds = meeting.end.timestamp - meeting.begin.timestamp;
        return formatDuration(diffInSeconds);
    };

    const checkAvailability = async () => {
        const meeting = getMeetingDetailsById(currentBookingData.meeting_id);
        if (!meeting) return;

        const requestId = availabilityRequestIdRef.current + 1;
        availabilityRequestIdRef.current = requestId;

        setSuccessMessage("");
        setErrorMessage("");
        setAvailabilityCode(-1);
        setIsLoading(true);

        const actualToken = await getAccessToken();
        const begin = new Date(`${dateValue} ${startTime}`).getTime();
        const end = begin + convertDurationToMilliseconds(duration);
        const format = "Y-m-d H:i:s";

        const payload = {
            object_ids: [meeting.object_id],
            timeframes: [
                {
                    begin: {
                        datetime: getFormattedDateTime(
                            begin,
                            format,
                            false,
                            userLocalTimezone
                        ),
                        timezone_id: meeting.begin.timezone_id,
                    },
                    end: {
                        datetime: getFormattedDateTime(
                            end,
                            format,
                            false,
                            userLocalTimezone
                        ),
                        timezone_id: meeting.end.timezone_id,
                    },
                },
            ],
            ignore_booking_ids: [meeting.booking_id],
            dummytoken: "",
            accesstoken: actualToken || "",
        };

        try {
            const response = await getTimeLinesApi(payload);

            if (requestId !== availabilityRequestIdRef.current) return;

            if (response.status === "success") {
                setAvailabilityCode(response.data[0].availability);
            } else {
                console.log("something went wrong!", response);
            }
        } catch (e) {
            if (requestId === availabilityRequestIdRef.current) {
                console.log("something went wrong!", e);
            }
        } finally {
            if (requestId === availabilityRequestIdRef.current) {
                setIsLoading(false);
            }
        }
    };

    const getMaxCapacity = (meeting_id: number) => {
        const meeting = getMeetingDetailsById(meeting_id);
        let maxCapacity = currentBookingData.meeting_participants;

        try {
            maxCapacity = meeting.seatings[0].capacity_max;
        } catch (e) {
            console.log(e);
        }

        return maxCapacity;
    };

    const closePopup = () => {
        if (action === "edit") {
            setEditBookingModal(false);
        } else {
            setRebookingModal(false);
        }

        updateSuccess && callback && callback();
    };

    return (
        <div className="edit-booking-container" data-testid={`editBooking-${action}`}>
            {isLoading && <LoaderComponent isLoading />}

            {action === "edit" ? (
                <div className="heading">{t("manageBooking.reschedule")}</div>
            ) : (
                <div className="heading">{t("manageBooking.bookAgain")}</div>
            )}

            {availabilityCode !== -1 && (
                <div className="availability-info">
                    {!successMessage && availabilityCode === 0 && (
                        <div className="heading info">
                            <ThemedIcon name="tick" />
                            <div className="text">{t("availability.free")}</div>
                        </div>
                    )}

                    {!successMessage && [1, 2].includes(availabilityCode) && (
                        <>
                            <div className="heading error">
                                <ThemedIcon name="cross_red" />
                                <div className="text">{t("availability.busy")}</div>
                            </div>
                            <div className="hint">{t("availability.busyHint")}</div>
                        </>
                    )}

                    {successMessage && action === "edit" && (
                        <div className="heading info">
                            <Icon name="success" />
                            <div className="text">{t("updateBookingApiSucessMsg")}</div>
                        </div>
                    )}

                    {successMessage && action === "rebook" && (
                        <div className="heading info">
                            <Icon name="success" />
                            <div className="text">{t("rebookingApiSucessMsg")}</div>
                        </div>
                    )}
                </div>
            )}

            <div className="title">
                {action === "edit" ? (
                    currentBookingData.meeting_desc
                ) : (
                    <EonUiInput
                        type="text"
                        size="small"
                        className="tab-input"
                        onValueChanged={(e: EonUiInputCustomEvent<any>) => {
                            setMeetingTopic(e.target.value);
                            setInvalidMeetingTopic(false);
                        }}
                        invalid={invalidMeetingTopic}
                        error-message={t("fieldRequired")}
                        placeholder={t("titleOfMeetingPlaceholder")}
                        label={t("titleOfMeetingLabel")}
                        labelOutside
                        value={meetingTopic}
                    />
                )}
            </div>

            <div className="other-details">
                <div className="location">
                    <div className="label">{t("manageBooking.meetingLocation")}</div>
                    <div className="loc">{currentBookingData.meeting_location}</div>
                </div>

                {action === "edit" && (
                    <div className="timing">
                        <div className="date">{formattedMeetingDate}</div>
                        <div className="time">{currentBookingData.meeting_timing}</div>
                        <div className="duration">
                            {t("duration")}:{" "}
                            {getMeetingDuration(currentBookingData.meeting_id)}
                        </div>
                    </div>
                )}
            </div>

            {action === "edit" && (
                <div className="reschedule-to">{t("manageBooking.rescheduleTo")}</div>
            )}

            <div className="form">
                <div className="row">
                    <div className="date">
                        <EonUiDatePicker
                            area-placeholder="datepicker"
                            onValueChanged={(e) => {
                                markScheduleChanged();
                                setDateValueChanged(true);
                                setDateValue(e.target.value);
                            }}
                            onDatePickerOpened={() => {
                                setPreFillComplete(true);
                            }}
                            value={dateValue}
                            name="date"
                            size="small"
                            label={t("date")}
                            labelOutside={true}
                            maxDaysBefore={0}
                            selectToday={true}
                            dateFormat="dd/MM/yyyy"
                            lang={i18n.language}
                            key={`${i18n.language}-${dateValue}`}
                        />
                    </div>

                    <div className="start-time dropdown">
                        {dateValue && (
                            <TimePicker
                                date={dateValue}
                                time={startTime}
                                nearestTime={dateValueChanged ? "" : startTime}
                                onValueChangedCallback={(timeValue: string) => {
                                    markScheduleChanged();
                                    setStartTime(timeValue);
                                }}
                                onDropdownOpenedCallback={() => {
                                    setPreFillComplete(true);
                                }}
                            />
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="duration dropdown">
                        <EonUiDropdown
                            area-placeholder="dropdown"
                            onValueChanged={(e) => {
                                markScheduleChanged();
                                setDuration(e.target.value);
                            }}
                            onDropdownOpened={() => {
                                setPreFillComplete(true);
                            }}
                            value={duration}
                            name="dropdown"
                            size="small"
                            label={t("duration")}
                            labelOutside={true}
                        >
                            {DURATION_OPTIONS.map((val, index) => (
                                <EonUiDropdownOption
                                    area-placeholder="option"
                                    key={index.toString()}
                                    value={val}
                                    label={t(`durationOptions.${val}`)}
                                />
                            ))}
                        </EonUiDropdown>
                    </div>

                    {action === "rebook" &&
                        currentBookingData.meeting_type === "Meeting Room" && (
                            <div className="participants">
                                <EonUiInput
                                    trailingIconName="user_group_alt"
                                    type="number"
                                    label={`${t("participants")} (${t("max")} ${getMaxCapacity(
                                        currentBookingData.meeting_id
                                    )})`}
                                    labelOutside
                                    placeholder="Ex. 3 or 5"
                                    value={numberOfParticipantsRebooking}
                                    max={getMaxCapacity(currentBookingData.meeting_id)}
                                    size="small"
                                    invalid={invalidNumberOfParticipants}
                                    error-message={t("fieldRequired")}
                                    onValueChanged={(e) => {
                                        setNumberOfParticipantsRebooking(e.target.value);
                                        setInvalidNumberOfParticipants(false);
                                    }}
                                />
                            </div>
                        )}
                </div>
            </div>

            {errorMessage && <div className="err-msg">{errorMessage}</div>}

            <div className="btn">
                <div className="confirm">
                    <EonUiButton
                        text={t("confirm")}
                        disabled={isLoading || !!errorMessage || availabilityCode !== 0}
                        onClick={() => {
                            if (action === "edit") {
                                updateBooking(currentBookingData.meeting_id);
                            } else {
                                saveBooking(currentBookingData.meeting_id);
                            }
                        }}
                    />
                </div>

                <div className="cancel">
                    <EonUiButton
                        rank="secondary"
                        text={successMessage ? t("close") : t("cancel")}
                        onClick={closePopup}
                    />
                </div>
            </div>
        </div>
    );
};

export default EditBooking;